import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Switch, Grid } from '@material-ui/core';
import { Button, ExportCSVButton, LoadingCircle, makeCsv, TitleBackButton } from '../common';
import { PERFORMANCE_LEVEL_COLORS } from '../common/performanceLevelColors';
import ProgressGoalsList from './ProgressGoalsList';
import styles from './ProgressGoals.styles';
import PgFilters from './PgFilters';
import { saveAs } from 'file-saver';

const isDemo = process.env.REACT_APP_THEME === 'demo';

const SUMMARY_SUBJECTS = [
  { key: 'reading', label: 'Reading' },
  { key: 'math', label: 'Math' },
];

// STAAR performance levels low -> high, with the canonical TEA colors (the two "High" sublevels
// reuse the StudentRow palette). Cards render in this order, skipping levels with no students.
const PG_LEVELS = [
  { key: 'doesNotMeet', label: 'Did Not Meet', color: PERFORMANCE_LEVEL_COLORS.doesNotMeet },
  { key: 'doesNotMeetHigh', label: 'Did Not Meet High', color: '#C27BA0' },
  { key: 'approaches', label: 'Approaches', color: PERFORMANCE_LEVEL_COLORS.approaches },
  { key: 'approachesHigh', label: 'Approaches High', color: '#B45F06' },
  { key: 'meets', label: 'Meets', color: PERFORMANCE_LEVEL_COLORS.meets },
  { key: 'masters', label: 'Masters', color: PERFORMANCE_LEVEL_COLORS.masters },
];

// Demo summary fallback: count each student's this-year goal/target level per subject, exclusively
// (one level per subject). Mirrors the server summary so cards look the same before it arrives —
// but the server version covers the whole filtered population, not just loaded rows.
const summarizeGoalsBySubject = (students) => {
  const summary = { math: { tested: 0 }, reading: { tested: 0 } };
  (students || []).forEach(([, info]) => {
    const goal = (info && info.goal) || {};
    SUMMARY_SUBJECTS.forEach(({ key: subject }) => {
      const curr = goal[subject] && goal[subject].curr;
      if (curr && !curr.error && curr.ammCode) {
        const level = curr.ammCode === 'max' ? 'masters' : curr.ammCode;
        summary[subject].tested += 1;
        summary[subject][level] = (summary[subject][level] || 0) + 1;
      }
    });
  });
  return summary;
};

class ProgressGoals extends Component {
  static propTypes = {
    students: PropTypes.array,
    summary: PropTypes.object,
    classes: PropTypes.object,
    loading: PropTypes.string.isRequired,
    loadingMore: PropTypes.string.isRequired,
    profile: PropTypes.object,
    avatars: PropTypes.object,
    errorMsg: PropTypes.any,
    errorCode: PropTypes.string,
    init: PropTypes.func,
    exportProgressGoals: PropTypes.func,
    getProgressGoals: PropTypes.func,
    filters: PropTypes.any,
    nextToken: PropTypes.any,
    progressGoalsCSV: PropTypes.object,
    loadingCSV: PropTypes.bool,
    recalculateProgressGoals: PropTypes.func,
  };

  state = {
    isPercent: true,
    prevSelectedFilters: '',
  };

  inited = false;

  componentDidMount() {
    this.shouldInit();
  }

  componentDidUpdate = async (prevProps) => {
    const { progressGoalsCSV, loadingCSV } = this.props;
    this.shouldInit();
    if (progressGoalsCSV && prevProps.loadingCSV && prevProps.loadingCSV !== loadingCSV) {
      const { data, columns } = progressGoalsCSV;
      const output = await makeCsv(data, columns);
      const blob = new Blob([output], { type: 'text/csv;charset=utf-8;' });
      if (blob) {
        saveAs(blob, 'Progress-Goals.csv');
      }
    }
  };

  togglePercentSwitch = () => {
    const { isPercent } = this.state;
    this.setState({ isPercent: !isPercent });
  };

  shouldInit = () => {
    const { profile, init, getProgressGoals, filters } = this.props;
    const { prevSelectedFilters } = this.state;
    const jsonSelectedFilters = JSON.stringify(filters);
    if (profile && !this.inited) {
      this.inited = true;
      getProgressGoals(filters);
      init(profile);
      this.setState({
        prevSelectedFilters: jsonSelectedFilters,
      });
    } else if (this.inited && jsonSelectedFilters !== prevSelectedFilters) {
      this.setState({
        prevSelectedFilters: jsonSelectedFilters,
      });
      getProgressGoals(filters);
    }
  };

  onLoadMore = () => {
    const { getProgressGoals, filters, nextToken } = this.props;
    if (nextToken) {
      getProgressGoals(filters, nextToken);
    }
  };

  exportCsv = () => {
    const { exportProgressGoals, filters } = this.props;
    exportProgressGoals(filters);
  };

  onRecalculatePG = () => {
    const { recalculateProgressGoals, filters } = this.props;
    recalculateProgressGoals(filters);
  };

  // Per-subject goal/target level breakdown (demo only). One colored card per level present.
  renderLevelSummary = (summary) => {
    const { classes } = this.props;
    if (!summary) return null;
    return SUMMARY_SUBJECTS.map(({ key: subject, label }) => {
      const subjSummary = summary[subject] || {};
      const levels = PG_LEVELS.filter((lvl) => (subjSummary[lvl.key] || 0) > 0);
      if (!levels.length) return null;
      return (
        <div key={subject} className={classes.pgSubjectGroup}>
          <div className={classes.pgSubjectHeading}>
            {label}
            <span className={classes.pgSubjectCount}>{` · ${(
              subjSummary.tested || 0
            ).toLocaleString()} with a goal`}</span>
          </div>
          <div className={classes.statCardRow}>
            {levels.map((lvl) => (
              <div key={lvl.key} className={classes.statCard} style={{ borderTop: `4px solid ${lvl.color}` }}>
                <div className={classes.statNumber} style={{ color: lvl.color }}>
                  {(subjSummary[lvl.key] || 0).toLocaleString()}
                </div>
                <div className={classes.statLabel}>{lvl.label}</div>
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  render() {
    const {
      classes,
      loading,
      profile,
      students,
      summary,
      avatars,
      loadingMore,
      errorMsg,
      errorCode,
      loadingCSV,
    } = this.props;
    const canRecalculate = profile && profile.role !== 'campusAdmin';
    const { isPercent } = this.state;

    const staarView = isPercent ? 'percentScore' : 'scaleScore';
    const showLoading = loading || !profile || !avatars;
    // Prefer the server summary (true totals over the whole filtered set); fall back to counting
    // the rows currently loaded if it hasn't arrived yet.
    const stats = isDemo ? summary || summarizeGoalsBySubject(students) : null;
    const list = (
      <ProgressGoalsList
        students={students}
        view={staarView}
        avatars={avatars}
        onLoadMore={this.onLoadMore}
        loading={loading || loadingMore}
      />
    );
    return (
      <div className={classes.root}>
        <TitleBackButton title="Real STAAR Progress Goals">
          {!loading && errorMsg ? (
            <Grid className={classes.errorRow}>
              <Typography className={classes.errorMsg} color="error">
                {errorMsg}
              </Typography>
              {/* "Not calculated yet" used to hide the only button that could fix it. */}
              {errorCode === 'noProgressGoals' && canRecalculate && (
                <Button color="outlined" large onClick={this.onRecalculatePG}>
                  Calculate Progress Goals
                </Button>
              )}
            </Grid>
          ) : (
            <Grid>
              {canRecalculate && (
                <Button color="outlined" large onClick={this.onRecalculatePG}>
                  Re-calculate Progress Goals
                </Button>
              )}
              <ExportCSVButton title="Export CSV" onClick={this.exportCsv} loading={loadingCSV} />
            </Grid>
          )}
        </TitleBackButton>
        {showLoading && (
          <div className={classes.loadingWrapper}>
            <LoadingCircle classes={classes} msg={loading} />
          </div>
        )}
        <div className={classes.content} style={{ display: showLoading ? 'none' : '' }}>
          <div className={classes.left}>
            <PgFilters />
            <Typography color="primary" className={classes.viewScore}>
              View Score
            </Typography>
            <div className={classes.viewRow}>
              <Typography color="primary" align="center">
                Scale Score
              </Typography>
              <Switch
                checked={isPercent}
                onChange={this.togglePercentSwitch}
                value="isPercent"
                classes={{
                  switchBase: classes.colorSwitchBase,
                  checked: classes.colorChecked,
                  bar: classes.colorBar,
                }}
              />
              <Typography color="primary" align="center">
                Percent
              </Typography>
            </div>
            <p className={classes.disclaimer}>
              Disclaimer: These reports and all data within are for illustrative purposes only and are subject to
              change. Official ratings and designations come only from the Texas Education Agency.
            </p>
          </div>
          {isDemo ? (
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {this.renderLevelSummary(stats)}
              {list}
            </div>
          ) : (
            list
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(ProgressGoals);
